/** RateCardsManager — versioned rate cards: master + brand overrides with
 *  validity dates, publish toggle, duplicate.
 */
import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Wallet, CheckCircle2, Circle, Copy, Trash2, Save, Building2, Globe, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

const fmtMoney = (v, cur = "INR") => v == null ? "—" : `${cur === "INR" ? "₹" : cur + " "}${Number(v).toLocaleString("en-IN")}`;

export default function RateCardsManager({ wsId }) {
  const [rows, setRows] = useState(null);
  const [brands, setBrands] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [editing, setEditing] = useState(null); // rate card doc or {} for new
  const [filter, setFilter] = useState({ kind: "all", brand_id: "all" });

  const load = () => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/rate-cards?include_unpublished=true`)
      .then((r) => setRows(r.data || [])).catch(() => setRows([]));
  };
  useEffect(() => {
    if (!wsId) return;
    load();
    api.get(`/workspaces/${wsId}/brands`).then((r) => setBrands(r.data || [])).catch(() => {});
    api.get(`/workspaces/${wsId}/designations`).then((r) => setDesignations(r.data || [])).catch(() => {});
  // eslint-disable-next-line
  }, [wsId]);

  const shown = useMemo(() => {
    return (rows || []).filter((r) => {
      if (filter.kind !== "all" && r.kind !== filter.kind) return false;
      if (filter.brand_id !== "all" && r.brand_id !== filter.brand_id) return false;
      return true;
    });
  }, [rows, filter]);

  const publish = async (r) => {
    try {
      await api.post(`/rate-cards/${r.id}/publish`, { is_published: !r.is_published });
      toast.success(r.is_published ? "Unpublished" : "Published");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Publish failed")); }
  };
  const duplicate = async (r) => {
    try {
      await api.post(`/rate-cards/${r.id}/duplicate`);
      toast.success("Duplicated (as unpublished draft)");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Duplicate failed")); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try {
      await api.delete(`/rate-cards/${r.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  return (
    <div className="space-y-4" data-testid="rate-cards-manager">
      <div className="flex flex-wrap items-center gap-2">
        <div className="text-sm text-muted-foreground flex-1">Versioned rate cards with validity dates. Brand overrides trump the master card when they cover the effective date.</div>
        <Select value={filter.kind} onValueChange={(v) => setFilter((f) => ({ ...f, kind: v }))}>
          <SelectTrigger className="h-8 w-40 rounded-none text-xs" data-testid="rc-filter-kind"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All kinds</SelectItem>
            <SelectItem value="master">Master only</SelectItem>
            <SelectItem value="brand_override">Brand overrides</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filter.brand_id} onValueChange={(v) => setFilter((f) => ({ ...f, brand_id: v }))}>
          <SelectTrigger className="h-8 w-56 rounded-none text-xs" data-testid="rc-filter-brand"><SelectValue placeholder="All brands" /></SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">All brands</SelectItem>
            {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button className="rounded-none" onClick={() => setEditing({})} data-testid="rc-new-btn">
          <Plus className="w-4 h-4 mr-1" />New rate card
        </Button>
      </div>

      {!rows ? <div className="text-sm text-muted-foreground p-6">Loading…</div>
        : shown.length === 0 ? (
          <div className="border p-8 text-center text-sm text-muted-foreground" data-testid="rc-empty">
            <Wallet className="w-8 h-8 mx-auto mb-2 opacity-50" />
            No rate cards. Create a Master rate card to get started, then layer brand overrides on top.
          </div>
        ) : (
          <div className="border divide-y" data-testid="rc-list">
            {shown.map((r) => (
              <div key={r.id} className="grid grid-cols-12 gap-2 px-3 py-2.5 items-center text-sm" data-testid={`rc-row-${r.id}`}>
                <button className="col-span-3 text-left hover:text-primary" onClick={() => setEditing(r)} data-testid={`rc-edit-${r.id}`}>
                  <div className="font-semibold flex items-center gap-2">
                    {r.kind === "master" ? <Globe className="w-3 h-3 text-sky-500" /> : <Building2 className="w-3 h-3 text-violet-500" />}
                    <span className="truncate">{r.name}</span>
                    <Badge variant="outline" className="rounded-none text-[9px]">v{r.version}</Badge>
                  </div>
                  {r.brand_name && <div className="text-[10px] text-muted-foreground truncate">{r.brand_name}</div>}
                </button>
                <div className="col-span-2 text-[10px] font-mono text-muted-foreground">
                  {r.effective_from} → {r.effective_to || "∞"}
                </div>
                <div className="col-span-2 text-xs">
                  {r._rates_hidden ? <span className="text-muted-foreground italic">rates hidden</span>
                    : <span>{Object.keys(r.rates || {}).length} designation{Object.keys(r.rates || {}).length === 1 ? "" : "s"}</span>}
                </div>
                <div className="col-span-1 text-xs">{r.currency}</div>
                <div className="col-span-2 flex items-center gap-2">
                  <button onClick={() => publish(r)} className={`text-[10px] inline-flex items-center gap-1 px-2 py-0.5 border rounded-none ${r.is_published ? "border-emerald-500/50 text-emerald-700" : "text-muted-foreground"}`} data-testid={`rc-publish-${r.id}`}>
                    {r.is_published ? <CheckCircle2 className="w-3 h-3" /> : <Circle className="w-3 h-3" />}
                    {r.is_published ? "Published" : "Draft"}
                  </button>
                </div>
                <div className="col-span-2 flex items-center gap-1 justify-end">
                  <button onClick={() => duplicate(r)} className="text-muted-foreground hover:text-foreground" title="Duplicate" data-testid={`rc-dup-${r.id}`}><Copy className="w-3.5 h-3.5" /></button>
                  <button onClick={() => remove(r)} className="text-destructive hover:opacity-75" title="Delete" data-testid={`rc-del-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      {editing !== null && (
        <RateCardDialog wsId={wsId} card={editing} brands={brands} designations={designations}
                         onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
    </div>
  );
}

function RateCardDialog({ wsId, card, brands, designations, onClose, onSaved }) {
  const isNew = !card.id;
  const [form, setForm] = useState({
    name: card.name || "",
    kind: card.kind || "master",
    brand_id: card.brand_id || "",
    effective_from: card.effective_from || new Date().toISOString().slice(0, 10),
    effective_to: card.effective_to || "",
    currency: card.currency || "INR",
    rates: card.rates || {},
    is_published: card.is_published || false,
    notes: card.notes || "",
  });
  const [customRow, setCustomRow] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setRate = (d, v) => setForm((s) => ({ ...s, rates: { ...s.rates, [d]: v === "" ? 0 : Number(v) } }));

  const rows = Array.from(new Set([...(designations || []), ...Object.keys(form.rates || {})])).sort();

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    if (!form.effective_from) { toast.error("Effective from is required"); return; }
    if (form.kind === "brand_override" && !form.brand_id) { toast.error("Pick a brand"); return; }
    setBusy(true);
    try {
      const body = {
        name: form.name.trim(),
        kind: form.kind,
        brand_id: form.kind === "brand_override" ? form.brand_id : null,
        effective_from: form.effective_from,
        effective_to: form.effective_to || null,
        currency: form.currency,
        rates: form.rates,
        is_published: form.is_published,
        notes: form.notes || null,
      };
      if (isNew) await api.post(`/workspaces/${wsId}/rate-cards`, body);
      else await api.put(`/rate-cards/${card.id}`, body);
      toast.success(isNew ? "Rate card created" : "Rate card updated");
      onSaved();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-none max-h-[92vh] overflow-y-auto" data-testid="rc-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "New rate card" : `Edit rate card · v${card.version}`}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Name</label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-none h-9" data-testid="rc-name" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Kind</label>
              <Select value={form.kind} onValueChange={(v) => set("kind", v)}>
                <SelectTrigger className="rounded-none h-9" data-testid="rc-kind"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="master">Master (workspace-wide)</SelectItem>
                  <SelectItem value="brand_override">Brand override</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.kind === "brand_override" && (
              <div className="md:col-span-2">
                <label className="text-[10px] uppercase text-muted-foreground">Brand</label>
                <Select value={form.brand_id} onValueChange={(v) => set("brand_id", v)}>
                  <SelectTrigger className="rounded-none h-9" data-testid="rc-brand"><SelectValue placeholder="Pick brand…" /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {brands.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Effective from</label>
              <Input type="date" value={form.effective_from} onChange={(e) => set("effective_from", e.target.value)} className="rounded-none h-9" data-testid="rc-effective-from" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Effective to (blank = open-ended)</label>
              <Input type="date" value={form.effective_to} onChange={(e) => set("effective_to", e.target.value)} className="rounded-none h-9" data-testid="rc-effective-to" />
            </div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Currency</label>
              <Input value={form.currency} onChange={(e) => set("currency", e.target.value.toUpperCase())} className="rounded-none h-9" data-testid="rc-currency" />
            </div>
            <label className="flex items-center gap-2 md:col-span-2 cursor-pointer">
              <input type="checkbox" checked={form.is_published} onChange={(e) => set("is_published", e.target.checked)} data-testid="rc-published" />
              <span>Published (available for package calculations)</span>
            </label>
          </div>

          <div className="border">
            <div className="px-3 py-2 border-b text-[10px] uppercase tracking-wider text-muted-foreground">
              Hourly rates ({rows.length} designations)
            </div>
            <div className="max-h-72 overflow-y-auto divide-y" data-testid="rc-rates-list">
              {rows.length === 0 && <div className="p-4 text-sm text-muted-foreground text-center">No designations yet.</div>}
              {rows.map((d) => (
                <div key={d} className="grid grid-cols-3 gap-2 px-3 py-2 items-center" data-testid={`rc-rate-${d}`}>
                  <div className="col-span-2 text-sm">{d}</div>
                  <Input type="number" min={0} value={form.rates[d] ?? 0} onChange={(e) => setRate(d, e.target.value)} className="h-8 rounded-none font-mono text-right" />
                </div>
              ))}
            </div>
            <div className="px-3 py-2 border-t flex gap-2">
              <Input value={customRow} onChange={(e) => setCustomRow(e.target.value)} placeholder="Add custom designation (e.g. Motion Designer)…" className="h-8 rounded-none text-xs" data-testid="rc-custom-input" />
              <Button size="sm" variant="outline" className="rounded-none h-8" onClick={() => { if (customRow.trim()) { setRate(customRow.trim(), 0); setCustomRow(""); } }} data-testid="rc-custom-add">
                <Plus className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Notes</label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={2} placeholder="Optional context…" className="rounded-none" data-testid="rc-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="rc-save">
            {busy && <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />}<Save className="w-3.5 h-3.5 mr-1" />Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
